'use client'

import Link from 'next/link'
import Image from 'next/image'
import styles from '@/styles/components/partsGroups/navigation/ResearcherHeader.module.scss'
import ModalEmailEdit from '@/components/partsGroups/modal/ModalEmailEdit'
import ModalPasswordEdit from '@/components/partsGroups/modal/ModalPasswordEdit'
import { appUrls } from '@/libs/appUrls'
import { useLocale } from '@/features/i18n/hooks/useLocale'
import { useAuth } from '@/components/providers/AuthProvider'
import { Menu, Popover } from '@headlessui/react'
import { useState } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import useSWR from 'swr'
import { apiUrls } from '@/libs/apiUrls'
import { ResultMsg } from '@/libs/types/ResultMsg'
import { TPersonInfo } from '@/libs/types/TPersonInfo'
import { TJobInfo } from '@/libs/types/TJobInfo'
import { commonUrls } from '@/libs/commonUrls'

export default function ResearcherHeader({
  className,
  currentMenu = 'home',
}: {
  className?: string
  currentMenu?: 'home' | 'profile' | 'search' | 'messages'
}) {
  const { locale, t } = useLocale()
  const { token, tUser } = useAuth()
  const { signOut } = useAuth()
  const router = useRouter()
  const path = usePathname()
  const query = useSearchParams()

  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)

  const languages = [
    { label: t.Japanese, value: 'ja' },
    { label: t.English, value: 'en' },
  ]
  const selectedLanguage = languages.find((lang) => lang.value === locale)

  const { data: userData } = useSWR(
    token ? [apiUrls.personal.getPersonInfo(), token, tUser?.id] : null,
    async ([url, token, userId]) => {
      if (!token || !userId) {
        return
      }
      const urlParams = new URLSearchParams({ personId: userId, token })
      const response = await fetch(`${url}?${urlParams.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
      })
      if (!response.ok) {
        throw new Error('Network response was not ok')
      }
      const result = (await response.json()) as ResultMsg<{
        personInfo: TPersonInfo
        jobInfoList: TJobInfo[]
        existingMail: string
      }>
      // console.log({ result })
      return result.extend
    },
  )

  return (
    <>
      <header className={styles.researcherHeader}>
        <Link href={appUrls.index(locale)} className={styles.researcherHeader__logo}>
          <Image src="/img/logo_coareseacher.png" alt="CoA Researcher" width={150} height={32} />
        </Link>
        <div className={styles.researcherHeader__content}>
          <select
            name=""
            id=""
            className={styles.researcherHeader__lang}
            defaultValue={selectedLanguage?.value}
            onChange={(e) => {
              const originalPath = path.replace('/ja', '').replace('/en', '')
              let redirectPath = `/${e.target.value}${originalPath}`

              if (query) {
                redirectPath += `?${query.toString()}`
              }

              router.push(redirectPath)
            }}
          >
            {languages.map((lang) => (
              <option key={lang.value} {...lang} />
            ))}
          </select>
          <div className={styles.researcherHeader__menu}>
            <Link
              href={appUrls.index(locale)}
              className={`${styles.researcherHeader__menu__link} ${
                currentMenu == 'home' && styles.current
              }`}
            >
              <span className="icon icon-home"></span>
              <p className={styles.researcherHeader__menu__text}>{t.header.Home}</p>
            </Link>
            <Link
              href={appUrls.profile(locale)}
              className={`${styles.researcherHeader__menu__link} ${
                currentMenu == 'profile' && styles.current
              }`}
            >
              <span className="icon icon-user"></span>
              <p className={styles.researcherHeader__menu__text}>{t.header.Profile}</p>
            </Link>
            <Link
              href={appUrls.search(locale)}
              className={`${styles.researcherHeader__menu__link} ${
                currentMenu == 'search' && styles.current
              }`}
            >
              <span className="icon icon-job"></span>
              <p className={styles.researcherHeader__menu__text}>{t.header.SearchJobs}</p>
            </Link>
            <Link
              href={appUrls.messages(locale)}
              className={`${styles.researcherHeader__menu__link} ${
                currentMenu == 'messages' && styles.current
              }`}
            >
              <span className="icon icon-mail"></span>
              <p className={styles.researcherHeader__menu__text}>{t.header.Messages}</p>
            </Link>
          </div>
          <Menu>
            <Menu.Button as="div" className={styles.researcherHeader__user}>
              {userData?.personInfo.headPortrait && (
                <div className={styles.userDetail__detail_title_icon}>
                  <Image
                    src={commonUrls.imagePathBase(
                      process.env.NEXT_PUBLIC_ENV,
                      userData.personInfo.headPortrait,
                    )}
                    alt={t.header.UserName}
                    width={38}
                    height={38}
                  />
                </div>
              )}
              {!userData?.personInfo.headPortrait && (
                <Image src="/img/no-image.png" alt={t.header.UserName} width={38} height={38} />
              )}
            </Menu.Button>
            <Menu.Items as="div" className={styles.researcherHeader__submenu__user}>
              <ul className={styles.researcherHeader__submenu__user__list}>
                <Menu.Item as="li" onClick={() => setIsPasswordModalOpen(true)}>
                  <a className={styles.researcherHeader__submenu__user__link}>
                    {t.header.EditPassword}
                  </a>
                </Menu.Item>
                <Menu.Item as="li" onClick={() => setIsEmailModalOpen(true)}>
                  <a className={styles.researcherHeader__submenu__user__link}>
                    {t.header.EditEmail}
                  </a>
                </Menu.Item>
                <Menu.Item as="li" onClick={() => signOut()}>
                  <a className={styles.researcherHeader__submenu__user__link}>
                    <span className="icon icon-logout"></span>
                    {t.header.Logout}
                  </a>
                </Menu.Item>
              </ul>
            </Menu.Items>
          </Menu>
        </div>
      </header>
      {/* メールアドレスの変更モーダル */}
      <ModalEmailEdit isOpen={isEmailModalOpen} onClose={() => setIsEmailModalOpen(false)} />
      {/* パスワードの変更モーダル */}
      <ModalPasswordEdit
        isOpen={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </>
  )
}
